// Idle-state center panel for Analyze (Piece C — replaces AnalyzeLaunchpad).
// Job: answer "where do I stand?" — advisor-driven next-step card on top
// (recommended + alternatives, same engine as Curate's "Next step" and the
// Where-am-I overlay), artifact state below. Launching tools lives in the rail
// nest; THIS surface is where runs are allowed to start.
import type { ReactNode } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ToolId, ToolStatus } from "@/lib/analyzeToolState";
import { STATUS_BADGE } from "@/components/analyze/toolMeta";
import { NextStepCard } from "@/components/where-am-i/NextStepCard";
import type { WorkflowRecommendation } from "@/lib/workflowAdvisor";

export interface ArtifactCard {
  id: ToolId;
  title: string;
  icon: ReactNode;
  iconColor: string;
  status: ToolStatus;
  /** Value proposition — why you'd run this tool (TOOL_META.value). */
  value: string;
  /** One-line state summary ("Generated May 28 — 3 camps · 2 tensions", "Not run yet"…). */
  summary: string;
  lockedReason?: string;
  onOpen?: () => void;
  onRerun?: () => void;
  rerunPending?: boolean;
  /** Button text for the rerun action (e.g. "Refresh — 220 new papers"). */
  rerunLabel?: string;
  /** Second line under the summary (e.g. the FD heavy-drift Rebuild nudge). */
  advisory?: string;
}

interface Props {
  recs: WorkflowRecommendation[];
  onActRec: (rec: WorkflowRecommendation) => void;
  artifacts: ArtifactCard[];
}

export function AnalyzeOverview({ recs, onActRec, artifacts }: Props) {
  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="mx-auto max-w-2xl space-y-4">
        {recs[0] && <NextStepCard rec={recs[0]} onAct={onActRec} />}

        <div className="px-1 pt-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Your analysis
        </div>
        {artifacts.map((a) => {
          const locked = a.status === "locked";
          return (
            <div
              key={a.id}
              data-tool={a.id}
              className={`rounded-md border bg-background px-3 py-2.5 ${locked ? "opacity-60" : ""}`}
            >
              <div className="flex items-start gap-2.5">
                <span
                  className="inline-flex size-7 flex-none items-center justify-center rounded"
                  style={{ background: `${a.iconColor}29`, color: a.iconColor }}
                >
                  {a.icon}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{a.title}</span>
                    <span
                      className={`rounded px-1.5 py-px text-[8.5px] font-bold tracking-wider ${STATUS_BADGE[a.status].className}`}
                    >
                      {STATUS_BADGE[a.status].label}
                    </span>
                  </div>
                  {/* Line 1: the tool's value — always shown so locked tools
                      still advertise what unlocking buys. Line 2: live state. */}
                  <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                    {a.value}
                  </p>
                  <div
                    className={`mt-1 text-[11px] ${
                      locked ? "text-muted-foreground/80" : "font-medium text-foreground/80"
                    }`}
                  >
                    {locked && a.lockedReason ? `🔒 ${a.lockedReason}` : a.summary}
                  </div>
                  {!locked && a.advisory && (
                    <div className="mt-0.5 text-[11px] text-amber-400/90">{a.advisory}</div>
                  )}
                </div>
                <div className="flex flex-none items-center gap-1">
                  {!locked && a.onOpen && (
                    <Button variant="ghost" size="sm" onClick={a.onOpen}>
                      Open
                    </Button>
                  )}
                  {!locked && a.onRerun && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={a.onRerun}
                      disabled={a.rerunPending}
                      aria-label={`Rerun ${a.title}`}
                    >
                      <RefreshCw
                        className={`size-3.5 ${a.rerunPending ? "animate-spin" : ""}`}
                      />
                      {a.rerunLabel ?? "Rerun"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
